// @context TGW-2195 入口の状態計算（entry_logic.js）の単体テスト。
//   ケース一覧は詳細設計書 §7.1 の表がそのまま対応する。
import {
  initialPage,
  MAX_REGEN,
  INITIAL_SUGGEST,
  canRegenerate,
  nextSuggestionState,
  bannerVisible,
  GENRE_DRAFTS,
  genreDraftFor,
  applyEntryDraftState,
  changeEntryTitleState,
  entryOperationLocked,
  historyDestination,
  shouldConfirmOverwrite,
  saveEntryTitle,
  generateEntryDraft,
  markAiAcceptedState,
} from './entry_logic';

describe('initialPage', () => {
  it('フラグ OFF なら従来の1本フォーム（0）', () => {
    expect(
      initialPage({ entryFlowEnabled: false, isNew: true, title: '' }),
    ).toBe(0);
  });

  it('ON × 新規 × タイトル空なら入口', () => {
    expect(
      initialPage({ entryFlowEnabled: true, isNew: true, title: '' }),
    ).toBe('entry');
  });

  it('ON × 新規 × タイトルありなら基本', () => {
    expect(
      initialPage({ entryFlowEnabled: true, isNew: true, title: 'カレー会' }),
    ).toBe('basic');
  });

  it('ON × 既存編集なら基本（入口は出さない）', () => {
    expect(
      initialPage({ entryFlowEnabled: true, isNew: false, title: '' }),
    ).toBe('basic');
  });

  it('タイトルが空白のみなら入口（trim 判定）', () => {
    expect(
      initialPage({ entryFlowEnabled: true, isNew: true, title: '  　' }),
    ).toBe('entry');
  });

  it('title が undefined でも落ちずに入口', () => {
    expect(
      initialPage({ entryFlowEnabled: true, isNew: true, title: undefined }),
    ).toBe('entry');
  });
});

describe('historyDestination', () => {
  it('入口から履歴を開いて戻る場合は入口へ戻る', () => {
    expect(historyDestination({ origin: 'entry', copied: false })).toBe(
      'entry',
    );
  });

  it('入口から履歴を開いてコピーした場合は基本ページへ進む', () => {
    expect(historyDestination({ origin: 'entry', copied: true })).toBe('basic');
  });

  it('基本ページから開いた履歴は戻る・コピーのどちらも基本ページへ戻る', () => {
    expect(historyDestination({ origin: 'basic', copied: false })).toBe(
      'basic',
    );
    expect(historyDestination({ origin: 'basic', copied: true })).toBe('basic');
  });

  it('フラグOFFの従来画面から開いた履歴は元の画面へ戻る', () => {
    expect(historyDestination({ origin: 0, copied: false })).toBe(0);
    expect(historyDestination({ origin: 0, copied: true })).toBe(0);
  });
});

describe('shouldConfirmOverwrite', () => {
  it('イベント名が空なら確認しない', () => {
    expect(shouldConfirmOverwrite({ title: '', editedByUser: true })).toBe(
      false,
    );
  });

  it('候補から入ったままなら確認しない', () => {
    expect(
      shouldConfirmOverwrite({
        title: 'AI候補のイベント名',
        editedByUser: false,
      }),
    ).toBe(false);
  });

  it('手入力したイベント名が残っていれば確認する', () => {
    expect(
      shouldConfirmOverwrite({
        title: '手入力のイベント名',
        editedByUser: true,
      }),
    ).toBe(true);
  });

  it('手入力後に空へ戻した場合は確認しない', () => {
    expect(shouldConfirmOverwrite({ title: '  　', editedByUser: true })).toBe(
      false,
    );
  });
});

describe('nextSuggestionState', () => {
  it('request で loading', () => {
    expect(nextSuggestionState(INITIAL_SUGGEST, { type: 'request' })).toEqual({
      ...INITIAL_SUGGEST,
      status: 'loading',
    });
  });

  it('success で ready になり shown に蓄積される', () => {
    const s = nextSuggestionState(
      { ...INITIAL_SUGGEST, status: 'loading' },
      { type: 'success', titles: ['A', 'B', 'C'], regen: false },
    );
    expect(s.status).toBe('ready');
    expect(s.titles).toEqual(['A', 'B', 'C']);
    expect(s.shown).toEqual(['A', 'B', 'C']);
    expect(s.regenCount).toBe(0);
  });

  it('再生成の success は regenCount が1増え、shown は積み上がる', () => {
    const first = nextSuggestionState(INITIAL_SUGGEST, {
      type: 'success',
      titles: ['A', 'B', 'C'],
      regen: false,
    });
    const second = nextSuggestionState(first, {
      type: 'success',
      titles: ['D', 'E', 'F'],
      regen: true,
    });
    expect(second.regenCount).toBe(1);
    expect(second.titles).toEqual(['D', 'E', 'F']);
    expect(second.shown).toEqual(['A', 'B', 'C', 'D', 'E', 'F']);
  });

  it('初回の failure は hidden（候補枠ごと出さない）', () => {
    const s = nextSuggestionState(
      { ...INITIAL_SUGGEST, status: 'loading' },
      { type: 'failure' },
    );
    expect(s.status).toBe('hidden');
  });

  it('再生成の failure は ready を維持（表示中の候補を消さない）', () => {
    const ready = nextSuggestionState(INITIAL_SUGGEST, {
      type: 'success',
      titles: ['A', 'B', 'C'],
      regen: false,
    });
    const s = nextSuggestionState(
      { ...ready, status: 'loading' },
      { type: 'failure' },
    );
    expect(s.status).toBe('ready');
    expect(s.titles).toEqual(['A', 'B', 'C']);
  });

  it('未知の action は状態を変えない', () => {
    expect(nextSuggestionState(INITIAL_SUGGEST, { type: 'unknown' })).toBe(
      INITIAL_SUGGEST,
    );
  });
});

describe('canRegenerate', () => {
  it('ready かつ regenCount が上限未満なら true', () => {
    expect(canRegenerate({ status: 'ready', regenCount: MAX_REGEN - 1 })).toBe(
      true,
    );
  });

  it('regenCount が上限に達したら false', () => {
    expect(canRegenerate({ status: 'ready', regenCount: MAX_REGEN })).toBe(
      false,
    );
  });

  it('status が ready 以外なら false', () => {
    ['idle', 'loading', 'hidden'].forEach((status) => {
      expect(canRegenerate({ status, regenCount: 0 })).toBe(false);
    });
  });
});

describe('bannerVisible', () => {
  it('AI 由来・ジャンル由来のたたき台では出す', () => {
    expect(bannerVisible('ai')).toBe(true);
    expect(bannerVisible('genre')).toBe(true);
  });

  it('過去コピー・手入力では出さない', () => {
    expect(bannerVisible('copy')).toBe(false);
    expect(bannerVisible(null)).toBe(false);
  });
});
describe('genreDraftFor', () => {
  const expectedTitles = {
    'スポーツ・運動': '気軽にスポーツ・運動を楽しむ交流会',
    'グルメ・食事会': 'みんなで食事と会話を楽しむ交流会',
    '友達作り・交流会': '気軽に話せる友達作り・交流イベント',
    アウトドア: 'みんなでアウトドアを楽しむ交流会',
    '音楽・ライブ': '音楽・ライブを一緒に楽しむ交流会',
    '勉強会・ビジネス': '一緒に学び交流できる勉強会・交流会',
  };

  it.each(Object.entries(expectedTitles))(
    '%s を所定のタイトルと本文を持つジャンル下書きへ変換する',
    (label, title) => {
      expect(genreDraftFor(label, '紹介文の定型')).toEqual({
        title,
        body: '紹介文の定型',
        source: 'genre',
        genre: label,
      });
    },
  );

  it('全ジャンルのタイトルが15〜60字に収まる', () => {
    Object.values(GENRE_DRAFTS).forEach((title) => {
      expect(title.length).toBeGreaterThanOrEqual(15);
      expect(title.length).toBeLessThanOrEqual(60);
    });
  });

  it('未入力の日時・場所・料金・人数を固定値として補わない', () => {
    Object.values(GENRE_DRAFTS).forEach((title) => {
      expect(title).not.toMatch(
        /\d{1,2}月|\d{1,2}日|\d{1,2}時|円|名限定|札幌|東京/,
      );
    });
  });
});

describe('入口タイトル補助の状態契約', () => {
  const initial = {
    titleText: '',
    bodyText: '',
    draftSource: null,
    genre: null,
    aiSuggested: { title: false, body: false },
  };

  it('ジャンルからAI候補へ切り替えると最後の候補だけが残る', () => {
    const genre = applyEntryDraftState(
      initial,
      genreDraftFor('グルメ・食事会', 'ジャンル本文'),
    );
    const ai = applyEntryDraftState(genre, {
      title: 'AI候補タイトル',
      source: 'ai',
    });

    expect(ai).toMatchObject({
      titleText: 'AI候補タイトル',
      bodyText: '',
      draftSource: 'ai',
      genre: null,
      aiSuggested: { title: true, body: false },
    });
  });

  it('AI生成からジャンルへ切り替えると最後のジャンルだけが残る', () => {
    const ai = applyEntryDraftState(initial, {
      title: 'AI生成タイトル',
      body: 'AI生成本文',
      source: 'ai',
    });
    const genre = applyEntryDraftState(
      ai,
      genreDraftFor('アウトドア', 'ジャンル本文'),
    );

    expect(genre).toMatchObject({
      titleText: GENRE_DRAFTS.アウトドア,
      bodyText: 'ジャンル本文',
      draftSource: 'genre',
      genre: 'アウトドア',
      aiSuggested: { title: false, body: false },
    });
  });

  it('タイトル編集ではタイトルのAI印だけを消し、本文の印は保持する', () => {
    const ai = applyEntryDraftState(initial, {
      title: 'AI生成タイトル',
      body: 'AI生成本文',
      source: 'ai',
    });

    expect(changeEntryTitleState(ai, '編集後タイトル')).toMatchObject({
      titleText: '編集後タイトル',
      bodyText: 'AI生成本文',
      draftSource: 'ai',
      aiSuggested: { title: false, body: true },
    });
  });
});

describe('saveEntryTitle', () => {
  it('次へで update_title を1回呼び、成功時だけ遷移処理を呼ぶ', async () => {
    const post = jest.fn().mockResolvedValue({});
    const onSaved = jest.fn();
    const onFailed = jest.fn();

    await expect(
      saveEntryTitle({
        post,
        eventId: 123,
        title: '  確定するイベント名  ',
        genre: 'アウトドア',
        onSaved,
        onFailed,
      }),
    ).resolves.toBe(true);

    expect(post).toHaveBeenCalledTimes(1);
    expect(post).toHaveBeenCalledWith(
      '/api/event_edit/update_title',
      JSON.stringify({
        event_id: 123,
        title: '確定するイベント名',
        genre: 'アウトドア',
      }),
      { timeout: 10000 },
    );
    expect(onSaved).toHaveBeenCalledWith('確定するイベント名');
    expect(onFailed).not.toHaveBeenCalled();
  });

  it('保存失敗時は遷移処理を呼ばず、失敗処理だけを呼ぶ', async () => {
    const post = jest.fn().mockRejectedValue(new Error('network'));
    const onSaved = jest.fn();
    const onFailed = jest.fn();

    await expect(
      saveEntryTitle({
        post,
        eventId: 123,
        title: '保持するイベント名',
        genre: null,
        onSaved,
        onFailed,
      }),
    ).resolves.toBe(false);

    expect(post).toHaveBeenCalledTimes(1);
    expect(onSaved).not.toHaveBeenCalled();
    expect(onFailed).toHaveBeenCalledTimes(1);
  });
});

describe('generateEntryDraft', () => {
  it('生成成功時はタイトルと本文をAI下書きとして返す', async () => {
    const post = jest.fn().mockResolvedValue({
      data: { result: { title: '生成タイトル', body: '生成本文' } },
    });

    await expect(
      generateEntryDraft({ post, theme: ' カレー ' }),
    ).resolves.toEqual({
      status: 'success',
      draft: { title: '生成タイトル', body: '生成本文', source: 'ai' },
    });
  });

  it('429と一般失敗を区別する', async () => {
    const rateLimitedPost = jest
      .fn()
      .mockRejectedValue({ response: { status: 429 } });
    const failedPost = jest.fn().mockRejectedValue(new Error('network'));

    await expect(
      generateEntryDraft({ post: rateLimitedPost, theme: 'カレー' }),
    ).resolves.toEqual({ status: 'rate_limited' });
    await expect(
      generateEntryDraft({ post: failedPost, theme: 'カレー' }),
    ).resolves.toEqual({ status: 'failure' });
  });

  it('保存中または生成中は入口操作をロックする', () => {
    expect(entryOperationLocked({ saving: false, aiGenerating: false })).toBe(
      false,
    );
    expect(entryOperationLocked({ saving: true, aiGenerating: false })).toBe(
      true,
    );
    expect(entryOperationLocked({ saving: false, aiGenerating: true })).toBe(
      true,
    );
  });
});

describe('markAiAcceptedState', () => {
  it('ジャンル由来の状態でAIのタイトルを反映すると、由来をAIにしてタイトルだけピル表示にする', () => {
    const state = { draftSource: 'genre', aiSuggested: { title: false, body: false } };
    expect(markAiAcceptedState(state, 'title')).toEqual({
      draftSource: 'ai',
      aiSuggested: { title: true, body: false },
    });
  });

  it('本文を反映すると、すでにAI由来のタイトルのピルは残す', () => {
    const state = { draftSource: 'ai', aiSuggested: { title: true, body: false } };
    expect(markAiAcceptedState(state, 'body').aiSuggested).toEqual({ title: true, body: true });
  });
});
